import { useAppConfig } from 'api/appConfig';
import LoadingPage from 'app/pages/Loading/LoadingPage';
import UnauthorizedPage from 'app/pages/Unauthorized/UnauthorizedPage';
import PageLayout from 'components/PageLayout/PageLayout';
import { ROUTES } from 'constants/routes';
import { useAuthContext } from 'contexts/AuthContext';
import { useModeTheme } from 'contexts/ModeThemeContext';
import { useInitIdpConfigContext } from 'hooks/initIdpConfigContext';
import { UnauthorizedReason } from 'types/api';
import { useAutoNavigateOnAppInit } from 'hooks/useAutoNavigateOnAppInit';
import React, { PropsWithChildren, Suspense, lazy, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useNav } from 'utils/navigation';
import { toRelativeRoute } from 'utils/routeUtils';

const LoginPage = lazy(() => import('app/pages/Login/LoginPage'));

const AppInit: React.FC<PropsWithChildren> = ({ children = null }) => {
	const { authRes } = useAuthContext();
	const {
		data: appConfig,
		isError: appConfigError,
		isLoading: appConfigLoading
	} = useAppConfig();
	const { themeLoaded } = useModeTheme();
	const idpContextInit = useInitIdpConfigContext();

	const nav = useNav();
	useAutoNavigateOnAppInit(idpContextInit && !!appConfig);

	const initError = (authRes.done && !authRes.verified) || appConfigError;
	const loader =
		initError ||
		!authRes.verified ||
		!themeLoaded ||
		!idpContextInit ||
		appConfigLoading;
	const loginFlowId =
		authRes.done &&
		authRes.unauthorizedReason === UnauthorizedReason.NO_AUTH &&
		!!authRes.projectId
			? appConfig?.loginFlowId || ''
			: '';

	useEffect(() => {
		// with a login flow the user can log in again from here, which needs the
		// /P<projectId> path, so stay put; the config tells whether there is one
		if (initError && !appConfig?.loginFlowId && !appConfigLoading) {
			nav(ROUTES.unauthorized);
		}
	}, [initError, appConfig?.loginFlowId, appConfigLoading, nav]);

	if (loginFlowId) {
		return (
			<PageLayout>
				<Suspense fallback={<LoadingPage />}>
					<LoginPage
						projectId={authRes.projectId as string}
						flowId={loginFlowId}
						styleId={appConfig?.loginFlowStyleId}
					/>
				</Suspense>
			</PageLayout>
		);
	}

	return loader ? (
		<Routes>
			<Route
				path={toRelativeRoute(ROUTES.unauthorized)}
				element={
					<PageLayout>
						<UnauthorizedPage />
					</PageLayout>
				}
			/>
			{themeLoaded && initError ? (
				<Route
					path="*"
					element={
						<PageLayout>
							<UnauthorizedPage />
						</PageLayout>
					}
				/>
			) : (
				<Route
					path="*"
					element={
						<PageLayout>
							<LoadingPage />
						</PageLayout>
					}
				/>
			)}
		</Routes>
	) : (
		children
	);
};

export default AppInit;
